import React from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { nanoid } from 'nanoid';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Stack, Button } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
//
import BlockContent from './BlockContent';
import RejectionFiles from './RejectionFiles';
import MultiFilePreview from './MultiFilePreview';

// ----------------------------------------------------------------------

const DropZoneStyle = styled('div')(({ theme }) => ({
  outline: 'none',
  padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  '&:hover': { opacity: 0.72, cursor: 'pointer' },
}));

// ----------------------------------------------------------------------

UploadMultiFile.propTypes = {
  error: PropTypes.bool,
  showPreview: PropTypes.bool,
  onUpload: PropTypes.func,
  isUploading: PropTypes.bool,
  afterUpload: PropTypes.func,
  onRemove: PropTypes.func,
  onRemoveAll: PropTypes.func,
  helperText: PropTypes.node,
  sx: PropTypes.object,
};

async function fileGetter(event) {
  const files = [];
  const fileList = event.dataTransfer
    ? event.dataTransfer.files
    : event.target.files;

  for (let i = 0; i < fileList.length; i++) {
    const file = fileList.item(i);

    // assign a unique value to the file in order to avoid multiple files rendering in the unmount animation
    Object.defineProperty(file, 'key', {
      value: nanoid(),
    });

    files.push(file);
  }

  return files;
}

export default function UploadMultiFile({
  error,
  showPreview = false,
  onUpload,
  isUploading,
  afterUpload,
  helperText,
  sx,
  ...other
}) {
  const [files, setFiles] = React.useState([]);

  const onDrop = React.useCallback((acceptedFiles) => {
    setFiles((f) => f.concat(acceptedFiles));
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
  } = useDropzone({
    ...other,
    onDrop,
    getFilesFromEvent: fileGetter,
  });

  const onRemoveFile = React.useCallback((index) => {
    setFiles((f) => f.filter((_, fileIndex) => fileIndex !== index));
  }, []);

  const onRemoveAllFiles = React.useCallback(() => {
    setFiles([]);
  }, []);

  const onUploadFiles = React.useCallback(async () => {
    await onUpload(files);

    if (afterUpload && typeof afterUpload === 'function') {
      afterUpload();
    }
  }, [files, onUpload, afterUpload]);

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', ...sx }}>
      <DropZoneStyle
        {...getRootProps()}
        sx={{
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            color: 'error.main',
            borderColor: 'error.light',
            bgcolor: 'error.lighter',
          }),
        }}
      >
        <input {...getInputProps()} />

        <BlockContent />
      </DropZoneStyle>

      {fileRejections.length > 0 && (
        <RejectionFiles fileRejections={fileRejections} />
      )}

      <MultiFilePreview
        files={files ?? []}
        showPreview={showPreview}
        onRemove={onRemoveFile}
      />

      {(files ?? []).length > 0 && (
        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <Button
            color="inherit"
            onClick={onRemoveAllFiles}
            disabled={isUploading}
          >
            Quitar todos los archivos
          </Button>
          {isUploading ? (
            <LoadingButton variant="contained" loading />
          ) : (
            <Button
              variant="contained"
              onClick={onUploadFiles}
              disabled={isUploading}
            >
              Guardar
            </Button>
          )}
        </Stack>
      )}

      {helperText && helperText}
    </Box>
  );
}
